import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Admin.css';

type AdminMode = 'users' | 'groups' | 'trash' | 'broadcast';

interface AdminSectionProps {
  mode: AdminMode;
}

interface SimpleUser {
  id: number;
  login: string;
  email?: string;
  is_admin?: boolean;
  is_active?: boolean;
  pseudonym?: string;
  full_name?: string;
  phone?: string;
  telegram_id?: string;
  groups?: Array<{id: number; group_name: string}>;
  bot_bindings?: {telegram?: boolean; max?: boolean};
}

interface SimpleGroup {
  id: number;
  group_name: string;
  icon_code?: string;
  sort?: number;
  is_active?: boolean;
  unread_count?: number;
  chats_count?: number;
  bot_binding?: string | {telegram?: number[]; max?: number[]};
}

interface TrashChat {
  id: number;
  display_name?: string;
  full_name?: string;
  first_name?: string;
  nickname?: string;
  chat_id?: string;
  last_message_at?: string;
}

interface BroadcastHistoryItem {
  timestamp: string;
  text: string;
  author?: { id: number; login: string; pseudonym?: string };
  groups: Array<{ id: number; group_name: string }>;
  files?: Array<{
    file_name?: string;
    type?: string;
    file_size?: number;
    hash?: string;
    preview_url?: string;
    download_url?: string;
  }>;
  sent?: number;
  failed?: number;
  total?: number;
}

interface ScheduledBroadcastItem {
  id: string;
  status: 'pending' | 'done' | 'failed' | 'cancelled';
  run_at: string;
  created_at?: string;
  executed_at?: string;
  text?: string;
  group_ids?: number[];
  files?: Array<{ file_name?: string }>;
  author?: { id: number; login: string; pseudonym?: string };
  error?: string;
}

const titles: Record<AdminMode, string> = {
  users: 'Персонал',
  groups: 'Группы',
  trash: 'Корзина',
  broadcast: 'Рассылка'
};

const AdminSection: React.FC<AdminSectionProps> = ({ mode }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [groups, setGroups] = useState<SimpleGroup[]>([]);
  const [trashChats, setTrashChats] = useState<TrashChat[]>([]);
  const [selectedTrashChatIds, setSelectedTrashChatIds] = useState<number[]>([]);
  const [selectedTrashUser, setSelectedTrashUser] = useState<string>('');
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastFiles, setBroadcastFiles] = useState<File[]>([]);
  const [broadcastHistory, setBroadcastHistory] = useState<BroadcastHistoryItem[]>([]);
  const [scheduledAt, setScheduledAt] = useState<string>('');
  const [scheduledBroadcasts, setScheduledBroadcasts] = useState<ScheduledBroadcastItem[]>([]);
  const [editingScheduleId, setEditingScheduleId] = useState<string>('');
  const [editingScheduleText, setEditingScheduleText] = useState<string>('');
  const [editingScheduleAt, setEditingScheduleAt] = useState<string>('');
  const [editingScheduleFiles, setEditingScheduleFiles] = useState<File[]>([]);
  const [editingScheduleCurrentFiles, setEditingScheduleCurrentFiles] = useState<any[]>([]);
  const [historyGroupFilter, setHistoryGroupFilter] = useState<string>('');
  const [historyDateFrom, setHistoryDateFrom] = useState<string>('');
  const [historyDateTo, setHistoryDateTo] = useState<string>('');
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string>('');

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingUser, setEditingUser] = useState<SimpleUser | null>(null);
  const [editingGroup, setEditingGroup] = useState<SimpleGroup | null>(null);

  const modeTitle = useMemo(() => titles[mode], [mode]);

  const loadUsers = async () => {
    const res = await api.get('/api/users');
    setUsers(Array.isArray(res.data) ? res.data : []);
  };

  const loadGroups = async () => {
    // Try to load groups from manage endpoint first
    try {
      const res = await api.get('/api/groups/manage');
      if (Array.isArray(res.data) && res.data.length > 0) {
        console.log('Loaded groups from /api/groups/manage:', res.data);
        setGroups(res.data);
        return;
      }
    } catch (e) {
      console.warn('Failed to load from /api/groups/manage:', e);
    }
    
    // Fallback to regular groups endpoint
    try {
      const res = await api.get('/api/groups');
      console.log('Loaded groups from /api/groups:', res.data);
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load groups:', e);
      setGroups([]);
    }
  };

  const loadTrash = async () => {
    const res = await api.get('/api/chats/trash');
    setTrashChats(Array.isArray(res.data) ? res.data : []);
  };

  const loadBroadcastHistory = async () => {
    try {
      const res = await api.get('/api/broadcast/history?limit=5');
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setBroadcastHistory(items);
    } catch (e) {
      console.warn('Failed to load broadcast history:', e);
      setBroadcastHistory([]);
    }
  };

  const loadScheduledBroadcasts = async () => {
    try {
      const res = await api.get('/api/broadcast/schedule?limit=20');
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setScheduledBroadcasts(items);
    } catch (e) {
      console.warn('Failed to load scheduled broadcasts:', e);
      setScheduledBroadcasts([]);
    }
  };

  const reload = async () => {
    setLoading(true);
    setNotice('');
    try {
      let errors: string[] = [];

      // Always try to load groups first since other sections depend on it.
      try {
        await loadGroups();
      } catch (e) {
        console.error('Failed to load groups:', e);
        errors.push('Не удалось загрузить список групп');
        setGroups([]);
      }

      if (mode === 'users') {
        try {
          await loadUsers();
        } catch (e) {
          console.error('Failed to load users:', e);
          errors.push('Не удалось загрузить список пользователей');
          setUsers([]);
        }
      } else if (mode === 'trash') {
        try {
          await loadTrash();
        } catch (e) {
          console.error('Failed to load trash:', e);
          errors.push('Не удалось загрузить корзину');
          setTrashChats([]);
        }
      } else if (mode === 'broadcast') {
        try {
          await loadBroadcastHistory();
        } catch (e) {
          console.error('Failed to load broadcast history:', e);
          errors.push('Не удалось загрузить историю рассылки');
          setBroadcastHistory([]);
        }

        try {
          await loadScheduledBroadcasts();
        } catch (e) {
          console.error('Failed to load scheduled broadcasts:', e);
          errors.push('Не удалось загрузить отложенные рассылки');
          setScheduledBroadcasts([]);
        }
      }

      if (errors.length > 0) {
        setNotice(errors.join('. '));
      }
    } catch (e) {
      console.error('Admin page load error:', e);
      setNotice('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setNotice('');
    setBroadcastText('');
    setBroadcastFiles([]);
    setScheduledAt('');
    setEditingScheduleId('');
    setEditingScheduleText('');
    setEditingScheduleAt('');
    setHistoryGroupFilter('');
    setHistoryDateFrom('');
    setHistoryDateTo('');
    setSelectedGroups([]);
    setSelectedTrashChatIds([]);
    setSelectedTrashUser('');
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const getTrashChatName = (chat: TrashChat) =>
    chat.display_name || chat.full_name || chat.first_name || chat.nickname || 'Без имени';

  const trashUsers = useMemo(() => {
    return Array.from(new Set(trashChats.map(getTrashChatName))).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [trashChats]);

  const filteredTrashChats = useMemo(() => {
    if (!selectedTrashUser) return trashChats;
    return trashChats.filter((c) => getTrashChatName(c) === selectedTrashUser);
  }, [trashChats, selectedTrashUser]);

  const historyGroupOptions = useMemo(() => {
    const map = new Map<number, string>();
    broadcastHistory.forEach((item) => {
      (item.groups || []).forEach((g) => {
        if (!map.has(g.id)) map.set(g.id, g.group_name);
      });
    });
    return Array.from(map.entries())
      .map(([id, group_name]) => ({ id, group_name }))
      .sort((a, b) => a.group_name.localeCompare(b.group_name, 'ru'));
  }, [broadcastHistory]);

  const filteredBroadcastHistory = useMemo(() => {
    return broadcastHistory.filter((item) => {
      if (historyGroupFilter) {
        const gid = Number(historyGroupFilter);
        if (!(item.groups || []).some((g) => g.id === gid)) return false;
      }

      if (historyDateFrom) {
        const fromDate = new Date(`${historyDateFrom}T00:00:00`);
        const ts = new Date(item.timestamp);
        if (ts < fromDate) return false;
      }

      if (historyDateTo) {
        const toDate = new Date(`${historyDateTo}T23:59:59`);
        const ts = new Date(item.timestamp);
        if (ts > toDate) return false;
      }

      return true;
    });
  }, [broadcastHistory, historyGroupFilter, historyDateFrom, historyDateTo]);

  const toggleTrashChat = (chatId: number) => {
    setSelectedTrashChatIds((prev) =>
      prev.includes(chatId) ? prev.filter((id) => id !== chatId) : [...prev, chatId]
    );
  };

  const toggleAllVisibleTrashChats = () => {
    const visibleIds = filteredTrashChats.map((c) => c.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedTrashChatIds.includes(id));

    if (allSelected) {
      setSelectedTrashChatIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    setSelectedTrashChatIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const deleteSelectedTrashChats = async () => {
    if (selectedTrashChatIds.length === 0) {
      setNotice('Выберите чаты для удаления');
      return;
    }
    if (!window.confirm(`Удалить выбранные чаты (${selectedTrashChatIds.length}) без возможности восстановления?`)) {
      return;
    }

    try {
      await Promise.all(selectedTrashChatIds.map((chatId) => api.delete(`/api/chats/${chatId}/purge`)));
      setNotice(`Удалено чатов: ${selectedTrashChatIds.length}`);
      setSelectedTrashChatIds([]);
      await loadTrash();
    } catch (e: any) {
      console.error('Trash purge error:', e);
      setNotice(e?.response?.data?.detail || 'Ошибка удаления чатов из корзины');
    }
  };

  const restoreSelectedTrashChats = async () => {
    if (selectedTrashChatIds.length === 0) {
      setNotice('Выберите чаты для восстановления');
      return;
    }

    try {
      await Promise.all(
        selectedTrashChatIds.map((chatId) => api.post(`/api/chats/${chatId}/move`, { trash: false }))
      );
      setNotice(`Восстановлено чатов: ${selectedTrashChatIds.length}`);
      setSelectedTrashChatIds([]);
      await loadTrash();
    } catch (e: any) {
      console.error('Trash restore error:', e);
      setNotice(e?.response?.data?.detail || 'Ошибка восстановления чатов из корзины');
    }
  };

  const toggleGroup = (groupId: number) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const sendBroadcast = async () => {
    if ((!broadcastText.trim() && broadcastFiles.length === 0) || selectedGroups.length === 0) {
      setNotice('Введите текст или добавьте файлы и выберите хотя бы одну группу');
      return;
    }

    setSending(true);
    setNotice('');
    try {
      const form = new FormData();
      form.append('message', broadcastText.trim());
      form.append('groups', JSON.stringify(selectedGroups));
      broadcastFiles.forEach((file) => form.append('files', file));

      const res = await api.post('/api/broadcast', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const sent = res?.data?.sent_count ?? res?.data?.sent ?? '';
      const failed = res?.data?.failed_count ?? res?.data?.failed ?? '';
      setNotice(`Рассылка отправлена${sent !== '' ? `: ${sent}` : ''}${failed !== '' ? `, ошибок: ${failed}` : ''}`);
      setBroadcastText('');
      setBroadcastFiles([]);
      await loadBroadcastHistory();
    } catch (e: any) {
      console.error('Broadcast error:', e);
      const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Ошибка рассылки';
      setNotice(String(msg));
    } finally {
      setSending(false);
    }
  };

  const scheduleBroadcast = async () => {
    if (!scheduledAt) {
      setNotice('Укажите дату и время для отложенной рассылки');
      return;
    }
    if ((!broadcastText.trim() && broadcastFiles.length === 0) || selectedGroups.length === 0) {
      setNotice('Введите текст или добавьте файлы и выберите хотя бы одну группу');
      return;
    }

    setSending(true);
    setNotice('');
    try {
      const form = new FormData();
      form.append('message', broadcastText.trim());
      form.append('groups', JSON.stringify(selectedGroups));
      form.append('run_at', scheduledAt);
      broadcastFiles.forEach((file) => form.append('files', file));

      await api.post('/api/broadcast/schedule', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNotice('Рассылка запланирована');
      setBroadcastText('');
      setBroadcastFiles([]);
      setScheduledAt('');
      await loadScheduledBroadcasts();
    } catch (e: any) {
      console.error('Schedule broadcast error:', e);
      const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Ошибка планирования рассылки';
      setNotice(String(msg));
    } finally {
      setSending(false);
    }
  };

  const cancelScheduledBroadcast = async (jobId: string) => {
    try {
      await api.post(`/api/broadcast/schedule/${jobId}/cancel`);
      setNotice('Запланированная рассылка отменена');
      await loadScheduledBroadcasts();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Ошибка отмены запланированной рассылки';
      setNotice(String(msg));
    }
  };

  const startEditScheduledBroadcast = (item: ScheduledBroadcastItem) => {
    setEditingScheduleId(item.id);
    setEditingScheduleText(item.text || '');
    setEditingScheduleAt(item.run_at ? String(item.run_at).slice(0, 16) : '');
    setEditingScheduleFiles([]);
    setEditingScheduleCurrentFiles(item.files || []);
  };

  const saveScheduledBroadcast = async () => {
    if (!editingScheduleId) return;
    if (!editingScheduleAt) {
      setNotice('Укажите дату и время');
      return;
    }

    try {
      const form = new FormData();
      form.append('text', editingScheduleText);
      form.append('run_at', editingScheduleAt);
      
      // Add information about current files to keep
      if (editingScheduleCurrentFiles.length > 0) {
        form.append('keep_files', JSON.stringify(editingScheduleCurrentFiles.map((f) => f.file_name || f.hash)));
      }
      
      // Add new files if any
      editingScheduleFiles.forEach((file) => {
        form.append('files', file);
      });

      await api.put(`/api/broadcast/schedule/${editingScheduleId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNotice('Запланированная рассылка обновлена');
      setEditingScheduleId('');
      setEditingScheduleText('');
      setEditingScheduleAt('');
      setEditingScheduleFiles([]);
      setEditingScheduleCurrentFiles([]);
      await loadScheduledBroadcasts();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Ошибка обновления запланированной рассылки';
      setNotice(String(msg));
    }
  };

  const openUserModal = (user?: SimpleUser) => {
    setEditingUser(user || null);
    setShowUserModal(true);
  };

  const openGroupModal = (group?: SimpleGroup) => {
    setEditingGroup(group || null);
    setShowGroupModal(true);
  };

  const deleteUser = async (userId: number) => {
    if (!window.confirm('Удалить пользователя?')) return;
    try {
      await api.delete(`/api/users/${userId}`);
      setNotice('Пользователь удалён');
      reload();
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const deleteGroup = async (groupId: number) => {
    if (!window.confirm('Удалить группу?')) return;
    try {
      await api.delete(`/api/groups/${groupId}`);
      setNotice('Группа удалена');
      reload();
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-inner">
        <div className="admin-header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => navigate('/dashboard')} className="header-btn" title="Назад">
              <i className="bi bi-arrow-left"></i>
            </button>
            <h2 style={{ margin: 0 }}>{modeTitle}</h2>
          </div>
          <div className="admin-header-actions">
            <button onClick={() => navigate('/admin/smtp')} className="header-btn" title="Настройки SMTP">
              <i className="bi bi-envelope"></i>
            </button>
            <button onClick={reload} className="header-btn" title="Обновить">
              <i className="bi bi-arrow-clockwise"></i>
            </button>
            <button onClick={logout} className="header-btn" title="Выход">
              <i className="bi bi-box-arrow-right"></i>
            </button>
          </div>
        </div>

        {notice && (
          <div style={{
            background: '#ecfeff',
            border: '1px solid #a5f3fc',
            color: '#155e75',
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: 12
          }}>
            {notice}
          </div>
        )}

        <div className="admin-card">
          {loading ? (
            <div style={{ color: '#6b7280' }}>Загрузка...</div>
          ) : mode === 'users' ? (
            <>
              <div className="admin-toolbar" style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => openUserModal()} className="logout-btn" style={{ width: 'auto', padding: '8px 14px' }}>
                  <i className="bi bi-plus-circle"></i> Добавить пользователя
                </button>
              </div>
              <div className="admin-table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8 }}>ID</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Логин</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Email</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Роль</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Статус</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Группы</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td style={{ padding: 8 }}>{u.id}</td>
                      <td style={{ padding: 8 }}>{u.login}</td>
                      <td style={{ padding: 8 }}>{u.email || '-'}</td>
                      <td style={{ padding: 8 }}>{u.is_admin ? 'Админ' : 'Оператор'}</td>
                      <td style={{ padding: 8 }}>{u.is_active ? 'Активен' : 'Отключен'}</td>
                      <td style={{ padding: 8, fontSize: 13 }}>
                        {u.groups && u.groups.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {u.groups.map(g => (
                              <span key={g.id} style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                                {g.group_name}
                              </span>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td style={{ padding: 8 }}>
                        <button onClick={() => openUserModal(u)} className="header-btn" title="Редактировать" style={{ marginRight: 4 }}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button onClick={() => deleteUser(u.id)} className="header-btn" title="Удалить">
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td style={{ padding: 8, color: '#6b7280' }} colSpan={7}>Пользователи не найдены</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </>
          ) : mode === 'groups' ? (
            <>
              <div className="admin-toolbar" style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => openGroupModal()} className="logout-btn" style={{ width: 'auto', padding: '8px 14px' }}>
                  <i className="bi bi-plus-circle"></i> Добавить группу
                </button>
              </div>
              <div className="admin-table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8 }}>ID</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Группа</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Иконка</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Порядок</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Боты</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Статус</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.id}>
                      <td style={{ padding: 8 }}>{g.id}</td>
                      <td style={{ padding: 8 }}>{g.group_name}</td>
                      <td style={{ padding: 8 }}><i className={`bi bi-${g.icon_code || 'inbox'}`}></i></td>
                      <td style={{ padding: 8 }}>{g.sort}</td>
                      <td style={{ padding: 8, fontSize: 13 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(() => {
                            const binding = g.bot_binding;
                            if (typeof binding === 'object' && binding !== null) {
                              const badges: React.ReactNode[] = [];
                              if (binding.telegram) {
                                binding.telegram.forEach((num: number) => {
                                  badges.push(<span key={`tg${num}`} style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>TG{num}</span>);
                                });
                              }
                              if (binding.max) {
                                binding.max.forEach((num: number) => {
                                  badges.push(<span key={`max${num}`} style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>MAX{num}</span>);
                                });
                              }
                              return badges.length > 0 ? badges : <span style={{ color: '#9ca3af' }}>-</span>;
                            } else {
                              // Legacy string format
                              if (binding === 'all' || binding === 'telegram') {
                                return <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>TG</span>;
                              }
                              if (binding === 'all' || binding === 'max') {
                                return <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>MAX</span>;
                              }
                            }
                          })()}
                        </div>
                      </td>
                      <td style={{ padding: 8 }}>{g.is_active ? 'Активна' : 'Отключена'}</td>
                      <td style={{ padding: 8 }}>
                        <button onClick={() => openGroupModal(g)} className="header-btn" title="Редактировать" style={{ marginRight: 4 }}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button onClick={() => deleteGroup(g.id)} className="header-btn" title="Удалить">
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {groups.length === 0 && (
                    <tr>
                      <td style={{ padding: 8, color: '#6b7280' }} colSpan={7}>Группы не найдены</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </>
          ) : mode === 'trash' ? (
            <>
              <div className="admin-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <label style={{ fontSize: 14, color: '#334155' }}>Пользователь:</label>
                <select
                  value={selectedTrashUser}
                  onChange={(e) => {
                    setSelectedTrashUser(e.target.value);
                    setSelectedTrashChatIds([]);
                  }}
                  style={{ minWidth: 220, border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px' }}
                >
                  <option value="">Все пользователи</option>
                  {trashUsers.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
                <button className="header-btn" onClick={toggleAllVisibleTrashChats} title="Выбрать/снять все">
                  <i className="bi bi-check2-square"></i>
                </button>
                <button
                  onClick={restoreSelectedTrashChats}
                  className="logout-btn"
                  style={{ width: 'auto', padding: '8px 14px', background: '#16a34a', borderColor: '#16a34a' }}
                  disabled={selectedTrashChatIds.length === 0}
                >
                  <i className="bi bi-arrow-counterclockwise" style={{ marginRight: 6 }}></i>
                  Восстановить выбранные ({selectedTrashChatIds.length})
                </button>
                <button
                  onClick={deleteSelectedTrashChats}
                  className="logout-btn"
                  style={{ width: 'auto', padding: '8px 14px', background: '#dc2626', borderColor: '#dc2626' }}
                  disabled={selectedTrashChatIds.length === 0}
                >
                  <i className="bi bi-trash3" style={{ marginRight: 6 }}></i>
                  Удалить выбранные ({selectedTrashChatIds.length})
                </button>
              </div>

              <div className="admin-table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8, width: 36 }}></th>
                    <th style={{ textAlign: 'left', padding: 8 }}>ID</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Пользователь</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>chat_id</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Последняя активность</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrashChats.map((c) => (
                    <tr key={c.id}>
                      <td style={{ padding: 8 }}>
                        <input
                          type="checkbox"
                          checked={selectedTrashChatIds.includes(c.id)}
                          onChange={() => toggleTrashChat(c.id)}
                        />
                      </td>
                      <td style={{ padding: 8 }}>{c.id}</td>
                      <td style={{ padding: 8 }}>{getTrashChatName(c)}</td>
                      <td style={{ padding: 8 }}>{String(c.chat_id || '')}</td>
                      <td style={{ padding: 8 }}>{c.last_message_at ? new Date(c.last_message_at).toLocaleString('ru-RU') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          ) : (
            <div>
              <div style={{ marginBottom: 12, color: '#334155' }}>Выберите группы и отправьте текст рассылки.</div>
              <div className="admin-broadcast-groups" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {groups.map((g) => (
                  <label key={g.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px' }}>
                    <input type="checkbox" checked={selectedGroups.includes(g.id)} onChange={() => toggleGroup(g.id)} />
                    {g.group_name}
                  </label>
                ))}
              </div>
              <textarea
                value={broadcastText}
                onChange={(e) => setBroadcastText(e.target.value)}
                rows={5}
                placeholder="Текст рассылки"
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, marginBottom: 10 }}
              />
              <div style={{ marginBottom: 10 }}>
                <input
                  type="file"
                  multiple
                  id="broadcast-file-input"
                  onChange={(e) => setBroadcastFiles(Array.from(e.target.files || []))}
                  style={{ display: 'none' }}
                />
                <label htmlFor="broadcast-file-input" style={{ display: 'inline-block', cursor: 'pointer' }}>
                  <button
                    type="button"
                    className="header-btn"
                    onClick={() => document.getElementById('broadcast-file-input')?.click()}
                  >
                    Выбрать файлы
                  </button>
                </label>
                {broadcastFiles.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#334155' }}>
                    Файлов выбрано: {broadcastFiles.length}
                    <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                      {broadcastFiles.map((f, idx) => (
                        <li key={`${f.name}-${idx}`}>{f.name} ({Math.round(f.size / 1024)} KB)</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <button onClick={sendBroadcast} disabled={sending} className="logout-btn" style={{ width: 'auto', padding: '8px 14px' }}>
                {sending ? 'Отправка...' : 'Отправить рассылку'}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 10px' }}
                />
                <button onClick={scheduleBroadcast} disabled={sending} className="header-btn" title="Запланировать рассылку">
                  <i className="bi bi-calendar2-plus"></i> Запланировать
                </button>
              </div>

              <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Запланированные рассылки</div>
                {scheduledBroadcasts.filter((x) => x.status === 'pending').length === 0 ? (
                  <div style={{ color: '#6b7280', fontSize: 14 }}>Нет активных задач</div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {scheduledBroadcasts.filter((x) => x.status === 'pending').map((item) => (
                      <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: '#fff' }}>
                        <div style={{ fontSize: 13, color: '#334155' }}>
                          Дата: {item.run_at ? new Date(item.run_at).toLocaleString('ru-RU') : '-'}
                        </div>
                        {item.author && (
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                            Запланировал: <span style={{ fontWeight: 500 }}>{item.author.pseudonym || item.author.login || 'неизвестно'}</span>
                            {item.author.login && <span> (@{item.author.login})</span>}
                          </div>
                        )}
                        <div style={{ fontSize: 13, marginTop: 4 }}>
                          Текст: {item.text || 'Без текста'}
                        </div>

                        {editingScheduleId === item.id && (
                          <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                            <textarea
                              value={editingScheduleText}
                              onChange={(e) => setEditingScheduleText(e.target.value)}
                              rows={3}
                              placeholder="Текст рассылки"
                              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 8 }}
                            />
                            <div>
                              {editingScheduleCurrentFiles.length > 0 && (
                                <div style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
                                  Текущие вложения:
                                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                                    {editingScheduleCurrentFiles.map((f, idx) => (
                                      <li key={`current-${idx}`}>
                                        {f.file_name} ({Math.round(f.file_size / 1024)} KB)
                                        <button
                                          type="button"
                                          onClick={() => setEditingScheduleCurrentFiles(editingScheduleCurrentFiles.filter((_, i) => i !== idx))}
                                          style={{ marginLeft: 6, background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer', fontSize: 12 }}
                                          title="Удалить"
                                        >
                                          ✕
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                <input
                                  type="file"
                                  multiple
                                  id="edit-broadcast-file-input"
                                  onChange={(e) => setEditingScheduleFiles(Array.from(e.target.files || []))}
                                  style={{ display: 'none' }}
                                />
                                <label htmlFor="edit-broadcast-file-input" style={{ display: 'inline-block', cursor: 'pointer' }}>
                                  <button
                                    type="button"
                                    className="header-btn"
                                    style={{ padding: '6px 10px', fontSize: 12 }}
                                    onClick={() => document.getElementById('edit-broadcast-file-input')?.click()}
                                  >
                                    Добавить файлы
                                  </button>
                                </label>
                              </div>
                              {editingScheduleFiles.length > 0 && (
                                <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
                                  Новые файлы: {editingScheduleFiles.length}
                                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                                    {editingScheduleFiles.map((f, idx) => (
                                      <li key={`new-${idx}`}>
                                        {f.name} ({Math.round(f.size / 1024)} KB)
                                        <button
                                          type="button"
                                          onClick={() => setEditingScheduleFiles(editingScheduleFiles.filter((_, i) => i !== idx))}
                                          style={{ marginLeft: 6, background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer', fontSize: 12 }}
                                          title="Удалить"
                                        >
                                          ✕
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <input
                                type="datetime-local"
                                value={editingScheduleAt}
                                onChange={(e) => setEditingScheduleAt(e.target.value)}
                                style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px' }}
                              />
                              <button className="header-btn" onClick={saveScheduledBroadcast}>
                                <i className="bi bi-check2"></i> Сохранить
                              </button>
                              <button
                                className="header-btn"
                                onClick={() => {
                                  setEditingScheduleId('');
                                  setEditingScheduleText('');
                                  setEditingScheduleAt('');
                                  setEditingScheduleFiles([]);
                                  setEditingScheduleCurrentFiles([]);
                                }}
                              >
                                <i className="bi bi-x"></i> Отмена
                              </button>
                            </div>
                          </div>
                        )}

                        <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button className="header-btn" onClick={() => startEditScheduledBroadcast(item)}>
                            <i className="bi bi-pencil"></i> Редактировать
                          </button>
                          <button className="header-btn" onClick={() => cancelScheduledBroadcast(item.id)}>
                            <i className="bi bi-x-circle"></i> Отменить
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <div style={{ fontWeight: 600 }}>Последние 5 рассылок</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <select
                      value={historyGroupFilter}
                      onChange={(e) => setHistoryGroupFilter(e.target.value)}
                      style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '4px 8px', fontSize: 13 }}
                    >
                      <option value="">Все группы</option>
                      {historyGroupOptions.map((g) => (
                        <option key={g.id} value={g.id}>{g.group_name}</option>
                      ))}
                    </select>
                    <input
                      type="date"
                      value={historyDateFrom}
                      onChange={(e) => setHistoryDateFrom(e.target.value)}
                      style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '4px 8px', fontSize: 13 }}
                      title="Дата от"
                    />
                    <input
                      type="date"
                      value={historyDateTo}
                      onChange={(e) => setHistoryDateTo(e.target.value)}
                      style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '4px 8px', fontSize: 13 }}
                      title="Дата до"
                    />
                  </div>
                </div>
                {filteredBroadcastHistory.length === 0 ? (
                  <div style={{ color: '#6b7280', fontSize: 14 }}>История пока пуста</div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {filteredBroadcastHistory.map((item, idx) => (
                      <div key={`${item.timestamp}-${idx}`} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', background: '#f8fafc' }}>
                        <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
                          {item.timestamp ? new Date(item.timestamp).toLocaleString('ru-RU') : '-'}
                        </div>
                        {item.author && (
                          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                            Отправил: <span style={{ fontWeight: 500 }}>{item.author.pseudonym || item.author.login || 'неизвестно'}</span>
                            {item.author.login && <span> (@{item.author.login})</span>}
                          </div>
                        )}
                        <div style={{ fontSize: 13, marginBottom: 4 }}>
                          Группы: {item.groups && item.groups.length > 0 ? item.groups.map(g => g.group_name).join(', ') : '-'}
                        </div>
                        <div style={{ fontSize: 14, marginBottom: 4, whiteSpace: 'pre-wrap' }}>
                          {item.text || <span style={{ color: '#6b7280' }}>Без текста</span>}
                        </div>
                        {item.files && item.files.length > 0 && (
                          <div style={{ fontSize: 13, color: '#334155', marginBottom: 4 }}>
                            <div style={{ marginBottom: 6 }}>Файлы:</div>
                            <div style={{ display: 'grid', gap: 6 }}>
                              {item.files.map((f, fIdx) => (
                                <div key={`${f.hash || f.file_name || 'file'}-${fIdx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <span>{f.file_name || 'Файл'}</span>
                                  {f.preview_url && (
                                    <a
                                      href={f.preview_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ color: '#0369a1', textDecoration: 'none', border: '1px solid #bae6fd', borderRadius: 6, padding: '2px 8px', background: '#f0f9ff' }}
                                    >
                                      Просмотр
                                    </a>
                                  )}
                                  {f.download_url && (
                                    <a
                                      href={f.download_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ color: '#166534', textDecoration: 'none', border: '1px solid #bbf7d0', borderRadius: 6, padding: '2px 8px', background: '#f0fdf4' }}
                                    >
                                      Скачать
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          Отправлено: {item.sent ?? 0}, Ошибок: {item.failed ?? 0}, Всего: {item.total ?? 0}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Modal */}
      {showUserModal && <UserModal user={editingUser} groups={groups} onClose={() => setShowUserModal(false)} onSave={() => { setShowUserModal(false); reload(); }} />}

      {/* Group Modal */}
      {showGroupModal && <GroupModal group={editingGroup} onClose={() => setShowGroupModal(false)} onSave={() => { setShowGroupModal(false); reload(); }} />}
    </div>
  );
};

// User Modal Component
const UserModal: React.FC<{user: SimpleUser | null; groups: SimpleGroup[]; onClose: () => void; onSave: () => void}> = ({ user, groups, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    login: user?.login || '',
    email: user?.email || '',
    password: '',
    is_admin: user?.is_admin || false,
    is_active: user?.is_active !== false,
    pseudonym: user?.pseudonym || '',
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    telegram_id: user?.telegram_id || '',
    groups: user?.groups?.map(g => g.id) || [] as number[],
    bot_bindings: user?.bot_bindings || {telegram: true, max: true}
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [credentialsMsg, setCredentialsMsg] = useState('');
  const [credentialsMsgOk, setCredentialsMsgOk] = useState(true);

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    const arr = new Uint8Array(14);
    window.crypto.getRandomValues(arr);
    const pwd = Array.from(arr).map(b => chars[b % chars.length]).join('');
    setFormData(f => ({...f, password: pwd}));
    setShowPassword(true);
  };

  const sendCredentials = async () => {
    if (!formData.password.trim()) {
      setCredentialsMsgOk(false);
      setCredentialsMsg('Сначала введите или сгенерируйте пароль');
      return;
    }
    if (!user?.id) return;
    setSendingCredentials(true);
    setCredentialsMsg('');
    try {
      const r = await api.post(`/api/users/${user.id}/send_credentials`, {
        password: formData.password.trim(),
        login: formData.login.trim() || user.login,
      });
      setCredentialsMsgOk(true);
      setCredentialsMsg((r.data as any)?.message || 'Отправлено');
    } catch (e: any) {
      setCredentialsMsgOk(false);
      setCredentialsMsg(e?.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setSendingCredentials(false);
    }
  };

  const handleSave = async () => {
    if (!formData.login.trim() || !formData.email.trim()) {
      setError('Логин и email обязательны');
      return;
    }
    if (!user && !formData.password.trim()) {
      setError('Пароль обязателен для нового пользователя');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        login: formData.login.trim(),
        email: formData.email.trim(),
        is_admin: formData.is_admin,
        is_active: formData.is_active,
        pseudonym: formData.pseudonym.trim() || null,
        full_name: formData.full_name.trim() || null,
        phone: formData.phone.trim() || null,
        telegram_id: formData.telegram_id.trim() || null,
        groups: formData.groups,
        bot_bindings: formData.bot_bindings
      };

      if (formData.password.trim()) {
        (payload as any).password = formData.password.trim();
      }

      if (user) {
        await api.put(`/api/users/${user.id}`, payload);
      } else {
        await api.post('/api/users', payload);
      }
      onSave();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (groupId: number) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.includes(groupId) ? prev.groups.filter(id => id !== groupId) : [...prev.groups, groupId]
    }));
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, maxWidth: 500, width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>{user ? 'Редактировать пользователя' : 'Новый пользователь'}</h3>
        {error && <div style={{ color: '#dc2626', marginBottom: 10 }}>{error}</div>}
        
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Логин *</label>
          <input type="text" value={formData.login} onChange={e => setFormData({...formData, login: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Email *</label>
          <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>ФИО</label>
          <input type="text" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Телефон</label>
          <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Telegram ID</label>
          <input type="text" value={formData.telegram_id} onChange={e => setFormData({...formData, telegram_id: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Пароль {!user && '*'}</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              placeholder={user ? 'Оставьте пустым, чтобы не менять' : ''}
              style={{ flex: 1, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="header-btn"
              title={showPassword ? 'Скрыть' : 'Показать'}
              style={{ padding: '8px 10px', flexShrink: 0 }}
            >
              <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button
              type="button"
              onClick={generatePassword}
              className="header-btn"
              style={{ padding: '6px 12px', fontSize: 13 }}
              title="Сгенерировать случайный пароль"
            >
              <i className="bi bi-shuffle" style={{ marginRight: 5 }}></i>Сгенерировать
            </button>
            {user?.id && user?.email && (
              <button
                type="button"
                onClick={sendCredentials}
                disabled={sendingCredentials}
                className="header-btn"
                style={{ padding: '6px 12px', fontSize: 13 }}
                title={`Отправить логин и пароль на ${user.email}`}
              >
                <i className="bi bi-envelope-arrow-up" style={{ marginRight: 5 }}></i>
                {sendingCredentials ? 'Отправка...' : 'Отправить на email'}
              </button>
            )}
          </div>
          {credentialsMsg && (
            <div style={{
              marginTop: 6,
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 13,
              background: credentialsMsgOk ? '#ecfeff' : '#fef2f2',
              color: credentialsMsgOk ? '#0e7490' : '#dc2626',
              border: `1px solid ${credentialsMsgOk ? '#a5f3fc' : '#fca5a5'}`,
            }}>
              <i className={`bi bi-${credentialsMsgOk ? 'check-circle' : 'exclamation-triangle'}`} style={{ marginRight: 5 }}></i>
              {credentialsMsg}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Псевдоним</label>
          <input type="text" value={formData.pseudonym} onChange={e => setFormData({...formData, pseudonym: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={formData.is_admin} onChange={e => setFormData({...formData, is_admin: e.target.checked})} />
            Администратор
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
            Активен
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Доступ к ботам</label>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={formData.bot_bindings.telegram} onChange={e => setFormData({...formData, bot_bindings: {...formData.bot_bindings, telegram: e.target.checked}})} />
              Telegram
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={formData.bot_bindings.max} onChange={e => setFormData({...formData, bot_bindings: {...formData.bot_bindings, max: e.target.checked}})} />
              MAX
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
            <i className="bi bi-folder" style={{ marginRight: 6, fontSize: 16 }}></i>
            Доступ к группам чатов
          </label>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
            gap: 8,
            padding: 12,
            background: '#f9fafb',
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            {groups.filter(g => g.id && g.id > 0 && g.group_name && !g.group_name.includes('Корзина') && !g.group_name.includes('Мои чаты') && !g.group_name.includes('Без группы')).map(g => (
              <label 
                key={g.id} 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  border: '1px solid #d1d5db', 
                  borderRadius: 8, 
                  padding: '8px 12px',
                  background: formData.groups.includes(g.id) ? '#e0e7ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <input 
                  type="checkbox" 
                  checked={formData.groups.includes(g.id)} 
                  onChange={() => toggleGroup(g.id)}
                  style={{ cursor: 'pointer' }}
                />
                <i className={`bi bi-${g.icon_code || 'inbox'}`} style={{ fontSize: 16, color: '#6366f1' }}></i>
                <span style={{ fontSize: 14, fontWeight: formData.groups.includes(g.id) ? 500 : 400 }}>
                  {g.group_name}
                </span>
              </label>
            ))}
            {groups.filter(g => g.id > 0).length === 0 && (
              <div style={{ padding: 8, color: '#9ca3af', fontSize: 14 }}>Нет доступных групп</div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={handleSave} disabled={saving} className="logout-btn" style={{ width: 'auto', padding: '8px 16px' }}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button onClick={onClose} className="header-btn" style={{ padding: '8px 16px' }}>Отмена</button>
        </div>
      </div>
    </div>
  );
};

// Group Modal Component
const GroupModal: React.FC<{group: SimpleGroup | null; onClose: () => void; onSave: () => void}> = ({ group, onClose, onSave }) => {
  const buildFormData = (g: SimpleGroup | null) => {
    const initBinding = g?.bot_binding;

    let initTelegramBots: number[] = [];
    let initMaxBots: number[] = [];

    if (typeof initBinding === 'object' && initBinding !== null) {
      initTelegramBots = Array.isArray(initBinding.telegram)
        ? initBinding.telegram.map((n) => Number(n)).filter((n) => Number.isInteger(n) && n >= 1 && n <= 5)
        : [];
      initMaxBots = Array.isArray(initBinding.max)
        ? initBinding.max.map((n) => Number(n)).filter((n) => Number.isInteger(n) && n >= 1 && n <= 5)
        : [];
    } else if (typeof initBinding === 'string') {
      if (initBinding === 'all' || initBinding === 'telegram') {
        initTelegramBots = [1];
      }
      if (initBinding === 'all' || initBinding === 'max') {
        initMaxBots = [1];
      }
    } else {
      // Default for legacy groups without explicit binding
      initTelegramBots = [1];
      initMaxBots = [1];
    }

    return {
      group_name: g?.group_name || '',
      icon_code: g?.icon_code || 'inbox',
      sort: g?.sort || 100,
      is_active: g?.is_active !== false,
      telegram_bots: initTelegramBots,
      max_bots: initMaxBots
    };
  };

  const [formData, setFormData] = useState(buildFormData(group));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData(buildFormData(group));
    setError('');
  }, [group]);

  const toggleTelegramBot = (botNum: number) => {
    setFormData(prev => ({
      ...prev,
      telegram_bots: prev.telegram_bots.includes(botNum) 
        ? prev.telegram_bots.filter(n => n !== botNum)
        : [...prev.telegram_bots, botNum].sort()
    }));
  };

  const toggleMaxBot = (botNum: number) => {
    setFormData(prev => ({
      ...prev,
      max_bots: prev.max_bots.includes(botNum)
        ? prev.max_bots.filter(n => n !== botNum)
        : [...prev.max_bots, botNum].sort()
    }));
  };

  const handleSave = async () => {
    if (!formData.group_name.trim()) {
      setError('Название группы обязательно');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        group_name: formData.group_name.trim(),
        icon_code: formData.icon_code,
        sort: formData.sort,
        is_active: formData.is_active,
        bot_binding: {
          telegram: formData.telegram_bots,
          max: formData.max_bots
        }
      };

      if (group) {
        await api.put(`/api/groups/${group.id}`, payload);
      } else {
        await api.post('/api/groups', payload);
      }
      onSave();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, maxWidth: 500, width: '90%' }}>
        <h3 style={{ marginTop: 0 }}>{group ? 'Редактировать группу' : 'Новая группа'}</h3>
        {error && <div style={{ color: '#dc2626', marginBottom: 10 }}>{error}</div>}
        
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Название *</label>
          <input type="text" value={formData.group_name} onChange={e => setFormData({...formData, group_name: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Иконка (Bootstrap Icons)</label>
          <input type="text" value={formData.icon_code} onChange={e => setFormData({...formData, icon_code: e.target.value})} placeholder="inbox, folder, tag..." style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Порядок сортировки</label>
          <input type="number" value={formData.sort} onChange={e => setFormData({...formData, sort: parseInt(e.target.value) || 0})} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 14 }}>Привязка к ботам</label>
          
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 500, marginBottom: 6, fontSize: 13, color: '#0088cc' }}>
              <i className="bi bi-telegram" style={{ fontSize: 16, marginRight: 4 }}></i>
              Telegram
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingLeft: 20 }}>
              {[1, 2, 3, 4, 5].map(num => (
                <label key={`tg${num}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', border: '1px solid #dbeafe', borderRadius: 6, padding: '4px 8px', background: formData.telegram_bots.includes(num) ? '#dbeafe' : 'transparent' }}>
                  <input type="checkbox" checked={formData.telegram_bots.includes(num)} onChange={() => toggleTelegramBot(num)} />
                  <span style={{ fontSize: 13 }}>Бот {num}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontWeight: 500, marginBottom: 6, fontSize: 13, color: '#ff6b35' }}>
              <i className="bi bi-chat-dots" style={{ fontSize: 16, marginRight: 4 }}></i>
              MAX
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingLeft: 20 }}>
              {[1, 2, 3, 4, 5].map(num => (
                <label key={`max${num}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', border: '1px solid #fef3c7', borderRadius: 6, padding: '4px 8px', background: formData.max_bots.includes(num) ? '#fef3c7' : 'transparent' }}>
                  <input type="checkbox" checked={formData.max_bots.includes(num)} onChange={() => toggleMaxBot(num)} />
                  <span style={{ fontSize: 13 }}>Бот {num}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
            Активна
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={handleSave} disabled={saving} className="logout-btn" style={{ width: 'auto', padding: '8px 16px' }}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button onClick={onClose} className="header-btn" style={{ padding: '8px 16px' }}>Отмена</button>
        </div>
      </div>
    </div>
  );
};

export default AdminSection;
